import { useCallback, useRef, useState, type PointerEvent } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'

/**
 * Draggable before/after reveal — matches the live site's `.ba-slider`
 * behavior (master prompt §2.5/§5.3: "accessible before/after control").
 * Pointer drag + a visually-hidden native `<input type="range">` overlay
 * give full mouse/touch/keyboard/screen-reader support without duplicating
 * ARIA slider semantics by hand.
 */
export function BeforeAfterSlider({
  before,
  after,
  beforeAlt,
  afterAlt,
}: {
  before: string
  after: string
  beforeAlt: string
  afterAlt: string
}) {
  const { t } = useLanguage()
  const [pos, setPos] = useState(50)
  const containerRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const ratio = ((clientX - rect.left) / rect.width) * 100
    setPos(Math.min(100, Math.max(0, ratio)))
  }, [])

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    updateFromClientX(e.clientX)
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    updateFromClientX(e.clientX)
  }
  const onPointerUp = () => {
    draggingRef.current = false
  }

  return (
    <div
      ref={containerRef}
      // Fixed aspect ratio (not a fixed min-height) so the image is never
      // taller/shorter than its actual shape on narrow mobile widths —
      // object-contain + a background fill means the full picture always
      // shows, with letterboxing instead of the previous mobile cropping.
      className="relative aspect-[4/3] w-full select-none overflow-hidden rounded-lg bg-black/30"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      <img src={before} alt={beforeAlt} loading="lazy" className="absolute inset-0 h-full w-full object-contain" />
      {/* clip-path (not a width-based wrapper) so the after-image never rescales as pos changes —
          only the visible slice moves, and it stays correct across resizes without a JS listener. */}
      <img
        src={after}
        alt={afterAlt}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-contain"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />

      <div
        className="pointer-events-none absolute inset-y-0 w-0.5 bg-white/70"
        style={{ left: `${pos}%` }}
      />
      <div
        className="pointer-events-none absolute top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-background/80 text-foreground shadow-[0_0_16px_rgba(0,0,0,0.4)]"
        style={{ left: `${pos}%` }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 5l-6 7 6 7M16 5l6 7-6 7" />
        </svg>
      </div>

      <span className="pointer-events-none absolute left-3 top-3 rounded-pill bg-background/70 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-wide text-foreground-subtle backdrop-blur-sm">
        {t('ba-after')}
      </span>
      <span className="pointer-events-none absolute right-3 top-3 rounded-pill bg-background/70 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-wide text-foreground-subtle backdrop-blur-sm">
        {t('ba-before')}
      </span>

      <input
        type="range"
        min={0}
        max={100}
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label={`${t('ba-before')} / ${t('ba-after')}`}
        className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
      />
    </div>
  )
}
